import { useState, useEffect, useCallback } from 'react';
import {
    useTriggerSourceDatacenterInventoryMutation,
    useTriggerSourceVMsInventoryMutation,
    useGetSourceInventoryStatusQuery,
} from '../api';

/**
 * Custom hook to trigger source inventory and poll for results
 * Uses RTK Query's built-in polling instead of manual intervals
 *
 * @returns {Object} Hook state and control functions
 */
export const useSourceInventoryPolling = () => {
    const [inventoryId, setInventoryId] = useState(null);
    const [isTriggering, setIsTriggering] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);

    const [triggerDatacenterInventory] = useTriggerSourceDatacenterInventoryMutation();
    const [triggerVMsInventory] = useTriggerSourceVMsInventoryMutation();

    // RTK Query automatically polls when inventoryId is set
    const {
        data: inventoryStatus,
        isLoading,
        isFetching,
        error,
    } = useGetSourceInventoryStatusQuery(inventoryId, {
        pollingInterval: 3000, // Poll every 3 seconds
        skip: !inventoryId || isCompleted, // Skip if no inventoryId or if already completed
        refetchOnMountOrArgChange: true, // Refetch on mount/focus
    });

    // Stop polling when state is success or error
    useEffect(() => {
        if (inventoryStatus?.state === 'success' || inventoryStatus?.state === 'error') {
            setIsCompleted(true);
        }
    }, [inventoryStatus?.state]);

    // Trigger source inventory to fetch  datacenter/cloud metadata (datacenters, clusters, folders, etc.)
    const triggerMetadataFetch = useCallback(
        async (params) => {
            try {
                setIsTriggering(true);
                setIsCompleted(false);
                const result = await triggerDatacenterInventory(params).unwrap();
                setInventoryId(result.id);
                return result.id;
            } catch (err) {
                console.error('Error fetching source inventory metadata:', err);
                throw err;
            } finally {
                setIsTriggering(false);
            }
        },
        [triggerDatacenterInventory],
    );

    // Trigger VMs inventory to collect VM sources with optional filters and move group assignment
    const triggerSourcesRefresh = useCallback(
        async (params) => {
            try {
                setIsTriggering(true);
                setIsCompleted(false);
                const result = await triggerVMsInventory(params).unwrap();
                setInventoryId(result.id);
                return result.id;
            } catch (err) {
                console.error('Error refreshing sources:', err);
                throw err;
            } finally {
                setIsTriggering(false);
            }
        },
        [triggerVMsInventory],
    );

    // Reset for new fetch - clears inventory ID and stops polling
    const reset = useCallback(() => {
        setInventoryId(null);
        setIsCompleted(false);
    }, []);

    // Determine if polling is active
    const isPolling = (inventoryStatus?.state !== 'success' && inventoryStatus?.state !== 'error') || isFetching;

    // Completion status
    const isComplete = inventoryStatus?.state === 'success';
    const hasError = inventoryStatus?.state === 'error' || !!error;

    return {
        // Trigger functions
        triggerMetadataFetch,
        triggerSourcesRefresh,
        reset,

        // Status
        inventoryId,
        inventoryStatus,
        isTriggering,
        isPolling,
        isComplete,
        hasError,
        error,

        // Loading states
        isLoading: isTriggering || isLoading || isPolling,
    };
};
